import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DRAG_THRESHOLD = 6;

function ArrowButton({ dir, disabled, onClick, label, testId }) {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="focus-ring flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-navy shadow-sm transition-all hover:border-ocean hover:bg-sky active:scale-95 disabled:pointer-events-none disabled:opacity-30"
    >
      <Icon className="h-[18px] w-[18px]" aria-hidden />
    </button>
  );
}

/** Wheel → horizontal only while the row can still move that way; at the ends the page scrolls. */
function useWheelToHorizontal(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let settle = 0;
    const onWheel = (e) => {
      if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return; // native horizontal / pinch
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) return;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
      const d = e.deltaY * unit;
      if ((d > 0 && el.scrollLeft >= max - 1) || (d < 0 && el.scrollLeft <= 1)) return;
      e.preventDefault();
      el.dataset.free = "true"; // pause snapping so small wheel steps aren't pulled back
      el.scrollLeft += d;
      clearTimeout(settle);
      settle = setTimeout(() => delete el.dataset.free, 160);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      clearTimeout(settle);
      el.removeEventListener("wheel", onWheel);
    };
  }, [ref]);
}

/** Mouse click-and-drag scrolling; touch and pen stay native. A drag swallows the following click. */
function useMouseDrag(ref) {
  const suppressClick = useRef(false);
  const onPointerDown = useCallback((e) => {
    const el = ref.current;
    if (!el || e.pointerType !== "mouse" || e.button !== 0) return;
    const startX = e.clientX;
    const startLeft = el.scrollLeft;
    let dragging = false;
    const move = (ev) => {
      const dx = ev.clientX - startX;
      if (!dragging && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!dragging) {
        dragging = true;
        el.dataset.dragging = "true";
        el.dataset.free = "true";
      }
      ev.preventDefault();
      el.scrollLeft = startLeft - dx;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (!dragging) return;
      suppressClick.current = true;
      delete el.dataset.dragging;
      delete el.dataset.free; // snapping resumes and settles on the nearest card
      setTimeout(() => {
        suppressClick.current = false;
      }, 0);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, [ref]);
  const onClickCapture = useCallback((e) => {
    if (!suppressClick.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClick.current = false;
  }, []);
  return { onPointerDown, onClickCapture };
}

function useEdges(ref, deps) {
  const [edges, setEdges] = useState({ prev: false, next: false });
  const atStart = useRef(true);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    atStart.current = el.scrollLeft <= 1;
    const next = { prev: el.scrollLeft > 1, next: el.scrollLeft < max - 1 };
    setEdges((o) => (o.prev === next.prev && o.next === next.next ? o : next));
  }, [ref]);
  useLayoutEffect(() => {
    // Items added before the snapped card make the browser re-snap away from the start; keep the row at the start.
    const el = ref.current;
    if (el && atStart.current && el.scrollLeft > 1) el.scrollLeft = 0;
  }, deps);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [update, ...deps]);
  return edges;
}

/** Scroll by about one viewport of cards, landing on a card start. */
function pageBy(el, dir) {
  const pad = parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
  const kids = Array.from(el.children);
  const view = el.clientWidth - pad;
  let target;
  if (dir > 0) {
    const edge = el.scrollLeft + el.clientWidth;
    const k = kids.find((c) => c.offsetLeft + c.offsetWidth > edge + 1);
    target = k ? k.offsetLeft - pad : el.scrollWidth;
    if (target <= el.scrollLeft + 1) target = el.scrollLeft + view;
  } else {
    const goal = el.scrollLeft - view;
    const k = kids.find((c) => c.offsetLeft - pad >= goal - 1);
    target = k ? k.offsetLeft - pad : 0;
    if (target >= el.scrollLeft - 1) target = el.scrollLeft - view;
  }
  el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
}

/**
 * Horizontal, snap-scrolling row: native touch/trackpad, mouse wheel + drag, optional arrows beside a heading.
 * `header` replaces the default h2; `action` sits before the arrows; `arrows={false}` keeps a bare row.
 */
export default function HorizontalCarousel({ title, header, action, children, testId, ariaLabel, arrows = true, className = "", rowClassName = "gap-3", itemCount }) {
  const ref = useRef(null);
  useWheelToHorizontal(ref);
  const drag = useMouseDrag(ref);
  const edges = useEdges(ref, [itemCount ?? React.Children.count(children)]);
  const label = ariaLabel ?? title ?? "Carousel";
  const showArrows = arrows && (edges.prev || edges.next);
  const hasHeader = header || title || action || arrows;

  return (
    <section data-testid={testId} className={`min-w-0 ${className}`}>
      {hasHeader ? (
        <div className="mb-3 flex min-h-[44px] flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {header ?? (title ? <h2 className="t-h2 min-w-0 truncate">{title}</h2> : <span />)}
          <div className="flex shrink-0 items-center gap-2">
            {action}
            {showArrows ? (
              <>
                <ArrowButton dir={-1} testId={`${testId}-prev`} label={`Scroll ${label} left`} disabled={!edges.prev} onClick={() => pageBy(ref.current, -1)} />
                <ArrowButton dir={1} testId={`${testId}-next`} label={`Scroll ${label} right`} disabled={!edges.next} onClick={() => pageBy(ref.current, 1)} />
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <div
        ref={ref}
        role="region"
        aria-label={label}
        data-testid={`${testId}-track`}
        onPointerDown={drag.onPointerDown}
        onClickCapture={drag.onClickCapture}
        onDragStart={(e) => e.preventDefault()}
        className={`no-scrollbar relative flex touch-pan-x touch-pan-y select-none snap-x snap-proximity overflow-x-auto overscroll-x-contain [contain:inline-size] [&>*]:shrink-0 [&>*]:snap-start data-[dragging=true]:cursor-grabbing data-[free=true]:snap-none ${rowClassName}`}
      >
        {children}
      </div>
    </section>
  );
}
